import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Reusable 3-2-1-Go! countdown with audio beeps.
 *
 * Beeps are pre-scheduled on the Web Audio API timeline so the dedicated
 * audio thread handles timing — immune to main-thread jitter that causes
 * setTimeout-based beeps to cluster or drift under CPU load.
 */
export function useCountdown() {
  const [countdownValue, setCountdownValue] = useState<string | null>(null);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const countdownIdRef = useRef(0);
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledNodesRef = useRef<OscillatorNode[]>([]);

  const getAudioContext = useCallback(() => {
    if (typeof window === 'undefined') return null;
    if (audioContextRef.current) return audioContextRef.current;

    const AudioContextConstructor =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return null;

    const ctx = new AudioContextConstructor();
    audioContextRef.current = ctx;
    return ctx;
  }, []);

  const scheduleBeep = useCallback((context: AudioContext, frequency: number, time: number) => {
    if (context.state === 'closed') return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    gain.connect(context.destination);

    gain.gain.setValueAtTime(0.01, time);
    gain.gain.linearRampToValueAtTime(0.3, time + 0.02);
    gain.gain.linearRampToValueAtTime(0.001, time + 0.15);

    oscillator.start(time);
    oscillator.stop(time + 0.15);

    scheduledNodesRef.current.push(oscillator);
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
      scheduledNodesRef.current = scheduledNodesRef.current.filter((n) => n !== oscillator);
    };
  }, []);

  const cancelScheduledBeeps = useCallback(() => {
    for (const node of scheduledNodesRef.current) {
      try {
        node.stop();
      } catch {
        /* already stopped */
      }
    }
    scheduledNodesRef.current = [];
  }, []);

  const waitUntil = useCallback(
    (targetTime: number) =>
      new Promise<void>((resolve) => {
        const remaining = Math.max(0, targetTime - performance.now());
        timerIdRef.current = setTimeout(resolve, remaining);
      }),
    [],
  );

  const cancelCountdown = useCallback(() => {
    countdownIdRef.current += 1;
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }
    cancelScheduledBeeps();
    setCountdownValue(null);
    setIsCountingDown(false);
  }, [cancelScheduledBeeps]);

  const runCountdown = useCallback(async () => {
    const countdownId = countdownIdRef.current + 1;
    countdownIdRef.current = countdownId;
    setIsCountingDown(true);

    // Ensure AudioContext is running before scheduling beeps
    const context = getAudioContext();
    if (context) {
      if (context.state === 'suspended') {
        await context.resume().catch(() => undefined);
      }

      // Schedule ALL beeps upfront on the Web Audio timeline.
      // The audio thread runs at high priority and keeps precise timing
      // regardless of main-thread load — no more clustered or drifting beeps.
      const audioNow = context.currentTime;
      scheduleBeep(context, 440, audioNow);
      scheduleBeep(context, 440, audioNow + 1.0);
      scheduleBeep(context, 440, audioNow + 2.0);
      scheduleBeep(context, 880, audioNow + 3.0);
    }

    // Visual updates still use setTimeout (slight jitter is acceptable for UI)
    const start = performance.now();
    const steps = ['3', '2', '1'];
    for (let i = 0; i < steps.length; i++) {
      setCountdownValue(steps[i]);
      await waitUntil(start + (i + 1) * 1000);
      if (countdownIdRef.current !== countdownId) {
        cancelScheduledBeeps();
        return false;
      }
    }

    setCountdownValue('Go!');
    await waitUntil(start + 3600);
    if (countdownIdRef.current !== countdownId) {
      cancelScheduledBeeps();
      return false;
    }
    setCountdownValue(null);
    setIsCountingDown(false);
    return true;
  }, [getAudioContext, scheduleBeep, cancelScheduledBeeps, waitUntil]);

  useEffect(() => {
    return () => {
      cancelCountdown();
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [cancelCountdown]);

  return { countdownValue, isCountingDown, runCountdown, cancelCountdown };
}
